// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import React from 'react';
import message from '../messages';
import { ApiResponse, PdfState } from '../context/ContextWrapper';
import client from '../api/RestApi';

const downloadPdfWithName = (file: Blob, fileName: string): void => {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(file);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    if ((window.navigator as any).msSaveOrOpenBlob) {
        (window.navigator as any).msSaveOrOpenBlob(file, fileName);
    } else {
        link.click();
    }
};

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
    const binaryString = window.atob(base64),
        binaryLen = binaryString.length,
        bytes = new Uint8Array(binaryLen);
    for (let i = 0; i < binaryLen; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
};

const createFileName = (chosenAgreement: string): string => {
    const prefix =
        chosenAgreement === agreementTypes.electronicSigning
            ? message('signedPdfFileName')
            : message('manualPdfFileName');
    const date = new Date();
    const dateString = date.getDate() + '.' + (date.getMonth() + 1) + '.' + date.getFullYear();

    return prefix + dateString + '.pdf';
};

export const mapPdfErrorMessageContent = (pdfState: PdfState): React.ReactElement | null => {
    if (!pdfState.errorItems || pdfState.errorItems.length === 0) {
        return null;
    }

    return (
        <>
            <div className="opux-spacing-top-margin">{message('validationErrorMessage')}</div>

            <ul className="opux-spacing-bottom-margin" id="e-service-agreement-custom-list">
                {pdfState.errorItems.map((item: string) => {
                    if (item.includes('tupas')) {
                        return null;
                    }

                    let errorItemMessage = message(item);

                    if (item.includes('administrators')) {
                        errorItemMessage =
                            message('adminInformation_part_1') + message(item.substring(item.indexOf('.') + 1));
                    }

                    if (item.includes('adminPhoneNumber')) {
                        errorItemMessage = message('adminInformation_part_1') + message('phone');
                    }

                    return <li key={`error-${item}`}>{errorItemMessage}</li>;
                })}
            </ul>
        </>
    );
};

export const getPdf = (agreementType: string, setPdfState: any, manualSigning: boolean = false): void => {
    client
        .get('/pdf-agreement?manualSigning=' + manualSigning)
        .then((response: ApiResponse) => {
            if (response.status === 'FAILURE') {
                setPdfState({
                    isLoading: false,
                    error: true,
                    errorItems: response.body,
                });
            } else {
                const pdf = base64ToArrayBuffer(response.body as string);
                downloadPdfWithName(new Blob([pdf]), createFileName(agreementType));
                setPdfState({ isLoading: false, error: false });
            }
        })
        .catch(() => {
            setPdfState({ isLoading: false, error: true });
        });
};

export const agreementTypes = {
    manualSigning: 'ONLINE_SIGNED_AGREEMENT',
    electronicSigning: 'MANUALLY_SIGNED_AGREEMENT',
};
